import { Box, Link, Stack } from '@mui/material';
import { AxiosError } from 'axios';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import FormField from 'components/common/formikForm/formField';
import SubmitButton from 'components/common/formikForm/SubmitButton';
import PasswordStrengthIndicator from 'components/common/PasswordStrengthIndicator/PasswordStrengthIndicator';
import AuthLayout, { authSubmitButtonSx } from 'views/v2/auth/components/authLayout';

import api from 'api';
import { Permission } from 'api/models/response/permissions';
import PERMISSION from 'assets/permissions.json';
import ROLE from 'assets/userRoles.json';
import { useInflight } from 'hooks/useInflight';
import { setAlert } from 'redux/alert';
import { loadApplications } from 'redux/applications/applications-middleware';
import { authUser, logout } from 'redux/auth/auth-middleware';
import { AppDispatch, RootState } from 'redux/store';
import type { LoginInterface, ResetPasswordInterface } from 'types';
import { resetPasswordDefaultValues, resetPasswordValidationSchema } from 'utils/validations/auth';

/**
 * Public page after email verification succeeds.
 * Expects `email` and `verification` in localStorage (set by VerificationCode).
 * On success: sets password via auth code, then auto-logs the user in.
 */
export default function CreatePasswordV2() {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const store = useStore<RootState>();
    const { isAuthenticated } = useSelector((state: RootState) => state.auth);
    const { role, id: userId } = useSelector((state: RootState) => state.user.entity);
    const { permissionsList, permissionsLoaded } = useSelector(
        (state: RootState) => state.permissions
    );

    const [sessionMissing, setSessionMissing] = useState(false);

    const storedEmail = (() => {
        try {
            const raw = localStorage.getItem('email');
            return raw ? (JSON.parse(raw) as string) : '';
        } catch {
            return '';
        }
    })();

    useEffect(() => {
        ReactGA.send({
            hitType: 'pageview',
            page: `${window.location.pathname}`,
            title: 'CreatePassword',
        });
    }, []);

    // In-flight gated submit. A double-click / Enter race used to fire
    //   POST /set_password_with_auth_code  +  POST /login
    // twice. The hook drops the second call while the first is pending.
    const { pending: saving, run: runSubmit } = useInflight(
        async (_signal, values: ResetPasswordInterface) => {
            const code = localStorage.getItem('verification');
            const email = localStorage.getItem('email');
            if (!code || !email) {
                setSessionMissing(true);
                dispatch(
                    setAlert({
                        message:
                            'Your verification session has expired. Please verify your account again.',
                        type: 'error',
                    })
                );
                return;
            }

            let verificationCode: string;
            let userEmail: string;
            try {
                verificationCode = JSON.parse(code);
                userEmail = JSON.parse(email);
            } catch {
                setSessionMissing(true);
                dispatch(
                    setAlert({
                        message:
                            'Your verification session has expired. Please verify your account again.',
                        type: 'error',
                    })
                );
                return;
            }

            try {
                const res = await api.setPassword.setPasswordWithAuthCode(
                    userEmail,
                    values.password,
                    verificationCode
                );
                if (res.status !== 200) {
                    dispatch(
                        setAlert({
                            message: 'We could not set your password. Please try again.',
                            type: 'error',
                        })
                    );
                    return;
                }
            } catch (err) {
                const status = (err as AxiosError)?.response?.status;
                if (status === 400 || status === 403) {
                    setSessionMissing(true);
                    dispatch(
                        setAlert({
                            message:
                                'This verification link is invalid or has expired. Please verify your account again.',
                            type: 'error',
                        })
                    );
                } else {
                    dispatch(
                        setAlert({
                            message: 'Something went wrong. Please try again.',
                            type: 'error',
                        })
                    );
                }
                return;
            }

            // Password is saved. Attempt auto-login; on failure send to /login
            // (password is already set).
            const formData: LoginInterface = {
                email: userEmail.trim().toLowerCase(),
                password: values.password,
            };

            await dispatch(authUser(formData));

            if (!store.getState().auth.isAuthenticated) {
                localStorage.removeItem('verification');
                localStorage.removeItem('email');
                await dispatch(logout());
                dispatch(
                    setAlert({
                        message: 'Your password has been set. Please sign in.',
                        type: 'success',
                    })
                );
                navigate('/login');
            }
        }
    );

    const handleSubmit = async (values: ResetPasswordInterface) => {
        await runSubmit(values);
    };

    useEffect(() => {
        if (role && isAuthenticated && permissionsLoaded) {
            let cancelled = false;

            (async () => {
                let next = '/dashboard';
                if (role === ROLE.externalClient) {
                    if (
                        permissionsList.some(
                            (permission: Permission) => permission.name === PERMISSION.EDIT
                        )
                    ) {
                        next = `/visa-process/assessment`;
                    } else {
                        // Previous impl hardcoded `/visa-process/1/agreement`.
                        // Fetch applications and use the first case id instead.
                        next = `/visa-process/1/agreement`;
                        if (userId) {
                            try {
                                const action = await dispatch(loadApplications(String(userId)));
                                if (cancelled) return;
                                if (loadApplications.fulfilled.match(action)) {
                                    const firstId = action.payload.data?.[0]?.id;
                                    if (firstId) {
                                        next = `/visa-process/${firstId}/agreement`;
                                    }
                                }
                            } catch {
                                // Fall back to the previous hardcoded path.
                            }
                        }
                    }
                }
                if (cancelled) return;
                navigate(next);
                localStorage.removeItem('verification');
                localStorage.removeItem('email');
            })();

            return () => {
                cancelled = true;
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [role, isAuthenticated, permissionsLoaded, permissionsList.length]);

    const goToVerification = () => navigate('/user-verification');

    const backToLogin = (
        <Link
            component={RouterLink}
            to="/login"
            underline="hover"
            variant="body2"
            sx={{ fontWeight: 600, color: 'primary.main', py: 0.5 }}
        >
            Back to login
        </Link>
    );

    if (sessionMissing && !saving) {
        return (
            <AuthLayout
                title="Session expired"
                subtitle="Your verification session has expired or is invalid. Please verify your account again."
                footer={backToLogin}
            >
                <SubmitButton
                    type="button"
                    fullWidth
                    size="large"
                    sx={authSubmitButtonSx}
                    onClick={goToVerification}
                >
                    Verify again
                </SubmitButton>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout
            title="Set your password"
            subtitle="Create a secure password to protect your account"
            footer={backToLogin}
        >
            <Formik
                initialValues={resetPasswordDefaultValues}
                validationSchema={resetPasswordValidationSchema}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting, values }) => {
                    const busy = isSubmitting || saving;
                    return (
                        <Form noValidate aria-busy={busy}>
                            <Stack spacing={2}>
                                {storedEmail ? (
                                    <Box
                                        sx={{
                                            fontSize: 14,
                                            fontWeight: 500,
                                            color: 'text.secondary',
                                            bgcolor: 'action.hover',
                                            borderRadius: 1.5,
                                            px: 2,
                                            py: 1.25,
                                            wordBreak: 'break-all',
                                            textAlign: 'center',
                                        }}
                                    >
                                        {storedEmail}
                                    </Box>
                                ) : null}

                                <FormField
                                    name="password"
                                    label="New Password"
                                    type="password"
                                    disabled={busy}
                                    autoComplete="new-password"
                                />

                                <PasswordStrengthIndicator password={values.password} />

                                <FormField
                                    name="confirmPassword"
                                    label="Confirm Password"
                                    type="password"
                                    disabled={busy}
                                    autoComplete="new-password"
                                />

                                <SubmitButton
                                    loading={busy}
                                    disabled={busy}
                                    fullWidth
                                    size="large"
                                    sx={authSubmitButtonSx}
                                >
                                    Save Password
                                </SubmitButton>
                            </Stack>
                        </Form>
                    );
                }}
            </Formik>
        </AuthLayout>
    );
}
